import { Component, OnInit, Input, PLATFORM_ID, OnDestroy, ViewChild, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators, ValidatorFn, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isPlatformBrowser, AsyncPipe, KeyValuePipe } from '@angular/common';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { Event, Language, LanguageMap, ProcessType, Role, UserProfile } from '../../../models';
import { UserService } from '../../../services';
import { Observable, Subscription } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { AuthService } from '../../../services';
import { environment } from '../../../../environments/environment';
import { nationalityValidator, countryNameValidator, legalAgeValidator, EmailCheckValidator } from '../../../validators';
import { Utils } from '../../utils';
import { CustomBuyerFieldsComponent } from '../custom-buyer-fields/custom-buyer-fields.component';
import { MatCard, MatCardContent } from '@angular/material/card';
import { LoaderComponent } from '../loader/loader.component';
import { MatFormField, MatLabel, MatError, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TrimDirective } from '../../directives/trim.directive';
import { PhoneFormFieldComponent } from '../inputs/phone-form-field/phone-form-field.component';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { TextFormFieldComponent } from '../inputs/text-form-field/text-form-field.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { PaymentProviderType, UserPaymentProvider } from '../../../models/user-payment-provider';

@Component({
  selector: 'app-account-infos',
  templateUrl: './account-infos.component.html',
  styleUrls: ['./account-infos.component.scss'],
  standalone: true,
  imports: [
    MatCard,
    MatCardContent,
    TranslocoModule,
    FormsModule,
    ReactiveFormsModule,
    LoaderComponent,
    MatFormField,
    MatLabel,
    MatInput,
    TrimDirective,
    MatError,
    MatHint,
    PhoneFormFieldComponent,
    MatSelect,
    MatOption,
    MatAutocompleteTrigger,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatAutocomplete,
    TextFormFieldComponent,
    CustomBuyerFieldsComponent,
    MatCheckbox,
    MatTooltip,
    AsyncPipe,
    KeyValuePipe,
  ],
})
export class AccountInfosComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private userService = inject(UserService);
  authService = inject(AuthService);
  private translocoService = inject(TranslocoService);
  private emailCheckValidator = inject(EmailCheckValidator);

  @Input() public generalConditions: boolean;
  @Input() public specificProcess: ProcessType;
  @Input() public hideMangoCG = false;
  @Input() public hidePhone = false;
  @Input() public event: Event;
  @Input() public canSimplifyForm = false;
  // To pull customBuyerFields form
  @ViewChild(CustomBuyerFieldsComponent, { static: false })
  private CustomBuyerFieldsComponent: CustomBuyerFieldsComponent;
  @Input() public optionnalField: {
    commercialCommunication?: boolean,
    customCommercialCommunication?: LanguageMap,
  } = {commercialCommunication: false};

  user$: Observable<UserProfile>;
  environment = environment;
  processType = ProcessType;
  userRole = Role;
  private isBrowser = false;

  countries: {[key: string]: {code: string, nationality: string, name: string, mangoAllowed: string}} = {
    FR: {
      code: 'FR',
      mangoAllowed: 'true',
      nationality: 'Français',
      name: 'France',
    },
  };
  filteredNationalities: Observable<{[key: string]: {code: string, nationality: string, name: string}}>;
  filteredCountryOfResidence: Observable<{[key: string]: {code: string, nationality: string, name: string}}>;

  stepThirdFormGroup: UntypedFormGroup;

  availableDays: Array<number>;
  availableMonths: Array<string> = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  availableYears: Array<number>;

  userInfo: UserProfile;
  userSub: Subscription;
  translationSub: Subscription;
  langSub: Subscription;
  userId: number;
  currentLang: Language;

  Language = Language;

  isLoadingUserPersonalInfo = true;

  formError = '';
  public shouldSimplifyForm = false;

  private paymentProvider = PaymentProviderType.Mangopay;

  constructor() {
    const authService = this.authService;
    const platformId = inject(PLATFORM_ID);

    this.isBrowser = isPlatformBrowser(platformId);
    this.user$ = authService.getUser();
  }

  get userProfileForm() {
    return this.stepThirdFormGroup?.get('userProfile') as UntypedFormGroup;
  }

  nationalityOrder = (a, b): number => a.value.nationality.localeCompare(b.value.nationality);

  countryNameOrder = (a, b): number => a.value.name.localeCompare(b.value.name);

  filterMangoCountries = (translatedCountries: {[key: string]: {code; mangoAllowed; nationality; name}}) =>
    // We want to keep countries allowed by mangopay only. List available here :
    // https://support.mangopay.com/s/article/which-are-the-authorized-countries-where-you-can-process-payments
    Object.fromEntries(
      Object.entries(translatedCountries)?.filter((t) => (t[1] as {code; mangoAllowed; nationality; name}).mangoAllowed === 'true'),
    ) as {[key: string]: {code; mangoAllowed; nationality; name}};

  ngOnInit() {
    this.buildUserInfoForm();

    this.translationSub = this.translocoService.selectTranslateObject('countries')
      .subscribe((translatedCountries) => {
        this.countries = translatedCountries?.FR ?
          this.filterMangoCountries(translatedCountries) : this.countries;
      });
    this.langSub = this.translocoService.langChanges$.subscribe((lang: Language) => {
      this.currentLang = lang;
    });
    if (this.isBrowser) {
      this.getUserInfo();
    }

    /* init available years for card expiry date */
    const date = new Date();
    const currentYear = date.getFullYear();
    /* we suppose user are between 12 and 112 years old */
    this.availableYears = [...Array(100).keys()].map((n) => currentYear - n - 12);
    this.updateAvailableDays(null, null);
  }

  onDateChange() {
    const month = (this.userProfileForm.controls.birthday as UntypedFormGroup).controls.month.value;
    const year = (this.userProfileForm.controls.birthday as UntypedFormGroup).controls.year.value;
    this.updateAvailableDays(month, year);
  }

  updateAvailableDays(month, year?) {
    /* If month is unset, january, march, may, july, august, october, december : 31 days displayed */
    if (!month || [0, 1, 3, 5, 7, 8, 10, 12].includes(month)) {
      // produces array with value from 0 to 31 and then remove the 0
      this.availableDays = [...Array(32).keys()].slice(1);
    } else if ([4, 6, 9, 11].includes(month)) {
      /* If month is april, june, september, november : 30 days displayed */
      this.availableDays = [...Array(31).keys()].slice(1);
    } else if (month === 2) {
      /* If month is february, either 28 or 29 days */
      /* If year undefined or leap : 29 days displayed for february */
      if (!year || year % 4 === 0) {
        this.availableDays = [...Array(30).keys()].slice(1);
      } else {
        /* If year is not leap : 28 days displayed for february */
        this.availableDays = [...Array(29).keys()].slice(1);
      }
    }
  }

  private userAlreadyHaveAllInfos(user: UserProfile): boolean {
    return user && user.email && user.verifiedEmail && user.firstname && user.lastname && user.birthday;
  }

  setBirthday() {
    const day = (this.userProfileForm.controls.birthday as UntypedFormGroup).controls.day.value;
    const month = (this.userProfileForm.controls.birthday as UntypedFormGroup).controls.month.value;
    const year = (this.userProfileForm.controls.birthday as UntypedFormGroup).controls.year.value;
    // javascript months start at 0 and user values start at 1 so we need to delete this offset by substracting 1
    const birthday = Date.UTC( year, month - 1, day);
    this.userInfo.birthday = birthday;
  }

  getUserInfo() {
    this.isLoadingUserPersonalInfo = true;
    this.userSub = this.userService.getUserInfo()
      .subscribe( (userInfo) => {
        this.userInfo = new UserProfile(userInfo);
        // Here we check whether we can display the simplified form,
        // so that once the form has been filled in completely, it doesn't become simplified.
        this.shouldSimplifyForm = this.canSimplifyForm && this.userAlreadyHaveAllInfos(this.userInfo);
        this.userId = this.userInfo.id;
        let birthdayDay = null;
        let birthdayMonth = null;
        let birthdayYear = null;
        if (this.userInfo.birthday) {
          const birthday = new Date(this.userInfo.birthday);
          birthdayDay = birthday.getUTCDate();
          birthdayMonth = birthday.getUTCMonth() + 1;
          birthdayYear = birthday.getUTCFullYear();
        }
        let citizenship = this.countries.FR;
        if (this.userInfo.citizenship) {
          citizenship = this.countries[this.userInfo.citizenship] ?? citizenship;
        }
        let countryOfResidence = this.countries.FR;
        if (this.userInfo.countryOfResidence) {
          countryOfResidence = this.countries[this.userInfo.countryOfResidence];
        }
        this.userProfileForm.patchValue({
          firstname: this.userInfo.firstname,
          lastname: this.userInfo.lastname,
          email: this.userInfo.email,
          mobilePhone: this.userInfo.mobilePhone,
          birthday: {
            day: birthdayDay,
            month: birthdayMonth,
            year: birthdayYear,
          },
          citizenship,
          countryOfResidence,
        });
        if (this.userInfo.email && this.userInfo.email.length > 2 && this.userInfo.role !==  this.userRole.Anon) {
          this.userProfileForm.controls.email.disable();
        }
        const userPaymentProvider = this.userInfo.UserPaymentProviders
          ?.find( (p) => p.providerType === this.paymentProvider);
        this.stepThirdFormGroup.get('generalConditions').setValue({
          cgReelax: this.userInfo.generalConditions,
          cgMangopay: !!userPaymentProvider?.providerGeneralConditions,
          responsibleUser: false,
          commercialCommunication: this.optionnalField?.commercialCommunication ?? false,
        });
        if (this.userInfo.UserBankingInfo && this.userInfo.UserBankingInfo?.mangoKYCLevel === 'STRONG') {
          this.userProfileForm.controls.firstname.disable();
          this.userProfileForm.controls.lastname.disable();
          this.userProfileForm.controls.email.disable();
          this.userProfileForm.controls.birthday.disable();
          this.userProfileForm.controls.citizenship.disable();
        }

        this.filteredNationalities = this.userProfileForm.get('citizenship').valueChanges.pipe(
          startWith(this.userProfileForm.get('citizenship').value),
          map( (v) => ( typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'name') ) ? v.name : v),
          // eslint-disable-next-line no-underscore-dangle
          map((value) => this._filterCountry(value, 'nationality')),
          map( (c) => {
            if (c && Object.values(c).length === 1 && this.userProfileForm.get('citizenship')?.value?.code) {
              return {}; // avoid crash on init
            }
            return c;
          }),
        );
        this.filteredCountryOfResidence = this.userProfileForm.get('countryOfResidence').valueChanges.pipe(
          startWith(this.userProfileForm.get('countryOfResidence').value),
          map( (v) => ( typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'name') ) ? v.name : v),
          // eslint-disable-next-line no-underscore-dangle
          map( (value) => this._filterCountry(value, 'name')),
          map( (c) => {
            if (c && Object.values(c).length === 1 && this.userProfileForm.get('countryOfResidence')?.value?.code) {
              return {}; // avoid crash on init
            }
            return c;
          }),
        );

        this.isLoadingUserPersonalInfo = false;
      }, (error) => {
        // eslint-disable-next-line no-console
        console.log('Error into function getUserInfo on account : ', error);
        this.isLoadingUserPersonalInfo = false;
      });
  }

  private _filterCountry(
    value: string,
    field: string,
  ): {[key: string]: {code: string, nationality: string, name: string}} {
    if (!this.countries) {
      return {};
    }
    const filterValue = value.toLowerCase();
    let countriesArray = [];
    countriesArray = Object.values(this.countries)
      .filter((country) => country[field]?.toString().toLowerCase().indexOf(filterValue) !== -1);
    const matchingCountries: {[key: string]: {code: string, nationality: string, name: string}} = {};
    countriesArray.forEach((country) => {
      matchingCountries[country.code] = this.countries[country.code];
    });
    return matchingCountries;
  }

  displayCountryName(country) {
    return country ? country.name : '';
  }

  displayNationality(country) {
    return country ? country.nationality : '';
  }

  buildUserInfoForm() {
    const birthdayValidators: ValidatorFn[] = [];
    if (this.specificProcess === ProcessType.Selling) {
      birthdayValidators.push(legalAgeValidator);
    }
    this.stepThirdFormGroup = this.formBuilder.group({
      userProfile: this.formBuilder.group({
        firstname: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern(Utils.nameRegex),
        ]),
        lastname: new UntypedFormControl('', [
          Validators.required,
          Validators.pattern(Utils.nameRegex),
        ]),
        email: new UntypedFormControl('', {
          validators: [Validators.required, Validators.email],
          asyncValidators: [this.emailCheckValidator.validate.bind(this.emailCheckValidator)],
        }),
        birthday: this.formBuilder.group({
          day: new UntypedFormControl('', [
            Validators.required,
          ]),
          month: new UntypedFormControl('', [
            Validators.required,
          ]),
          year: new UntypedFormControl('', [
            Validators.required,
          ]),
        },
        {
          validators: birthdayValidators,
        }),
        citizenship: new UntypedFormControl('', [
          Validators.required,
        ]),
        countryOfResidence: new UntypedFormControl('', [
          Validators.required,
        ]),
      },
      {
        validators: [
          nationalityValidator,
          countryNameValidator,
        ],
      }),
      generalConditions: this.formBuilder.group({
        cgReelax: new UntypedFormControl('', [
          Validators.requiredTrue,
        ]),
        cgMangopay: new UntypedFormControl('', [
          Validators.requiredTrue,
        ]),
        responsibleUser: new UntypedFormControl('', [
          Validators.requiredTrue,
        ]),
        commercialCommunication: new UntypedFormControl('', []),
      }),
    });
  }

  checkCountry(event) {
    if (!this.countries) {
      return ;
    }
    const field = event.target.name;
    let errorName = '';
    let propertyName = '';
    if (field === 'citizenship') {
      errorName = 'wrongNationality';
      propertyName = 'nationality';
    } else if (field === 'countryOfResidence') {
      errorName = 'wrongCountryName';
      propertyName = 'name';
    } else {
      return;
    }
    // reset value if manually set
    const value = event.target.value;
    const country = Object.values(this.countries)?.find((c) => c[propertyName].toLowerCase() === value.toLowerCase());
    if (country) {
      this.userProfileForm.get(field).setValue(country);
    }
    // Check that there is no error in country name, if an error is detected it is sent to control
    if (!!this.userProfileForm.hasError(errorName) &&
    !this.userProfileForm.get(field).hasError(errorName)) {
      const errors = [];
      const basicErrors = this.userProfileForm.get(field).errors;
      if (basicErrors) {
        Object.keys(basicErrors).forEach( (error) => {
          errors[error] = true;
        });
      }
      errors[errorName] = true;
      this.userProfileForm.get(field).setErrors({
        ...errors,
      });
    } else if (!this.userProfileForm.hasError(errorName) &&
      !!this.userProfileForm.get(field).hasError(errorName)) {
      // if there is not any country name error anymore and we still store it into control errors
      // then we reset its errors
      this.userProfileForm.get(field).setErrors(null);
    }
  }

  cleanNationality() {
    this.userProfileForm.get('citizenship').setValue('');
  }

  cleanCountryOfResidence() {
    this.userProfileForm.get('countryOfResidence').setValue('');
  }

  getError(formControlName: string, subForm: string, subsubForm?: string) {
    let control;
    if (subsubForm) {
      control = ((this.stepThirdFormGroup.get(formControlName) as UntypedFormGroup)
        .get(subForm) as UntypedFormGroup
      )?.get(subsubForm);
    } else {
      control = (this.stepThirdFormGroup.get(formControlName) as UntypedFormGroup)?.get(subForm);
    }
    const hasError = !!control && !!control.touched && !!control.errors;
    if (hasError) {
      return Object.keys(control.errors)[0];
    }
    return null;
  }

  public onAccountEdit() {
    this.userProfileForm.markAllAsTouched();
    // If generalConditions are all enabled, we check if the whole form is valid
    if (this.generalConditions && !this.hideMangoCG && !this.stepThirdFormGroup.valid) {
      this.stepThirdFormGroup.get('generalConditions').markAllAsTouched();
      this.formError = 'invalidForm';
      return null;
    }
    // If generalConditions are enabled but we hideMangoCG
    // we check if userProfile formGroup is valid AND if cgReelax is valid
    if (this.generalConditions && this.hideMangoCG &&
      (!this.userProfileForm.valid || !this.stepThirdFormGroup.get('generalConditions').get('cgReelax').valid)) {
      this.stepThirdFormGroup.get('generalConditions').markAllAsTouched();
      this.formError = 'invalidForm';
      return null;
    }
    // If all generalConditions are disabled, then we only check if userProfile formGroup is valid
    if (!this.generalConditions && !this.userProfileForm.valid) {
      this.stepThirdFormGroup.get('generalConditions').markAllAsTouched();
      this.formError = 'invalidForm';
      return null;
    }
    this.formError = '';
    this.userInfo.firstname = this.userProfileForm.controls.firstname.value;
    this.userInfo.lastname = this.userProfileForm.controls.lastname.value;

    // reset email only if empty :
    if ( !this.userInfo.email ) {
      this.userInfo.email = this.userProfileForm.controls.email.value;
    }
    this.setBirthday();

    if (this.userInfo.role === this.userRole.Anon && this.specificProcess === this.processType.Buying) {
      // 'FR' is default value for purchases
      // However, even for purchases, if user is not Anonymous
      // we need him to fill real information for KYC matching later
      this.userInfo.citizenship = undefined;
      this.userInfo.countryOfResidence = undefined;
      // Anonymous could have changed his email
      this.userInfo.email = this.userProfileForm.controls.email.value;
    } else {
      this.userInfo.citizenship = this.userProfileForm.controls.citizenship.value.code;
      this.userInfo.countryOfResidence = this.userProfileForm.controls.countryOfResidence.value.code;
    }
    if (this.userInfo?.region && !['US', 'MX', 'CA'].includes(this.userInfo.countryOfResidence)) {
      // well reset region if not required anymore
      this.userInfo.region = '';
    }

    this.userInfo.generalConditions = this.stepThirdFormGroup.get('generalConditions').value.cgReelax;
    if (this.hideMangoCG) {
      const providerGeneralConditions = this.stepThirdFormGroup.get('generalConditions').value.cgMangopay ? new Date() : null;
      const userPaymentProvider = this.userInfo.UserPaymentProviders
        ?.find( (p) => p.providerType === this.paymentProvider);
      if (!userPaymentProvider) {
        this.userInfo.UserPaymentProviders.push(
          new UserPaymentProvider({providerType: this.paymentProvider, providerGeneralConditions}),
        );
      } else {
        userPaymentProvider.providerGeneralConditions = providerGeneralConditions;
      }
    }
    const commercialCommunication = this.stepThirdFormGroup.get('generalConditions').value.commercialCommunication;

    let customBuyerFields;
    if (this.event?.EventPersonalization?.customBuyerFields) {
      customBuyerFields = this.CustomBuyerFieldsComponent?.onEdit();
      if (customBuyerFields instanceof Error) {
        this.formError = customBuyerFields?.message;
        return null;
      }
    }
    return {
      userProfile: this.userInfo,
      options: {
        commercialCommunication,
        customBuyerFields,
      },
    };
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
    this.translationSub?.unsubscribe();
    this.langSub?.unsubscribe();
  }
}
